import type { ReactElement } from 'react';
import { Info } from 'lucide-react';
import { BROWSER_AI_NAVIGATION_TYPE_LABELS } from '@kirin/api-client/contracts/shadow-ai-browser-tools';
import { Badge } from '@/components/ui/badge';

/**
 * The subset of a browser AI tool row that carries KIR-3890 "why this was
 * flagged" evidence. Both the org and personal browser-tool rows satisfy this.
 */
export interface BrowserToolEvidenceData {
  domain: string;
  visitCount: number;
  realVisitCount: number;
  navigationType: string | null;
  sampleMatchedUrl: string | null;
  firstDetected: string | null;
  lastSeen: string | null;
  // KIR-3890 per-bucket visit counts (0/undefined on rows from older agents).
  realCount?: number;
  redirectCount?: number;
  addressBarCount?: number;
  adsTrackingCount?: number;
}

/** Short labels for the "how it was reached" breakdown, in display order. */
const NAV_BREAKDOWN: ReadonlyArray<{
  key: keyof Pick<
    BrowserToolEvidenceData,
    'realCount' | 'redirectCount' | 'addressBarCount' | 'adsTrackingCount'
  >;
  label: string;
}> = [
  { key: 'realCount', label: 'Real' },
  { key: 'redirectCount', label: 'Redirects' },
  { key: 'addressBarCount', label: 'Address bar' },
  { key: 'adsTrackingCount', label: 'Ads/tracking' },
];

/**
 * Builds the per-bucket breakdown string ("Real 12 · Redirect 3 · Ads/tracking
 * 40"), including only buckets with at least one visit. Returns null when no
 * per-bucket counts exist (rows from older agents) so the caller can fall back
 * to the single dominant label.
 */
function navigationBreakdown(evidence: BrowserToolEvidenceData): string | null {
  const parts = NAV_BREAKDOWN.filter(({ key }) => (evidence[key] ?? 0) > 0).map(
    ({ key, label }) => `${label} ${evidence[key]}`
  );
  return parts.length > 0 ? parts.join(' · ') : null;
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Turns the coarse agent navigation-type bucket into human copy (KIR-3890).
 * Falls back gracefully for rows produced by older agents (typed/link/other).
 */
function describeNavigation(navigationType: string | null): string {
  if (navigationType && navigationType in BROWSER_AI_NAVIGATION_TYPE_LABELS) {
    return BROWSER_AI_NAVIGATION_TYPE_LABELS[
      navigationType as keyof typeof BROWSER_AI_NAVIGATION_TYPE_LABELS
    ];
  }
  // Legacy buckets emitted by pre-KIR-3890 agents.
  switch (navigationType) {
    case 'typed':
      return 'Typed in the address bar';
    case 'link':
      return 'Followed a link';
    case 'other':
      return 'Direct visit';
    default:
      return 'Unknown';
  }
}

function EvidenceField({
  label,
  value,
}: {
  label: string;
  value: string;
}): ReactElement {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-sm font-medium">{value}</span>
    </div>
  );
}

/**
 * "Why this was flagged" evidence panel (KIR-3890, Mockup 1). Rendered inside
 * the browser-AI-tools row drill-down so an operator can see the concrete
 * signal — how many real visits, when, how the page was reached, and one sample
 * matched URL — instead of an opaque "detected" flag.
 */
export function BrowserToolEvidence({
  evidence,
}: {
  evidence: BrowserToolEvidenceData;
}): ReactElement {
  const { visitCount, realVisitCount, navigationType, sampleMatchedUrl } =
    evidence;
  const hasRealVisit = realVisitCount > 0;

  return (
    <div className="flex flex-col gap-3 rounded-md border bg-muted/30 p-4">
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold">Why this was flagged</span>
        <Badge
          variant={hasRealVisit ? 'default' : 'secondary'}
          className="text-xs"
        >
          {hasRealVisit ? 'Real visit' : 'No real visits'}
        </Badge>
      </div>

      <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
        <EvidenceField
          label="Visits"
          value={`${visitCount} (${realVisitCount} real)`}
        />
        <EvidenceField
          label="First seen"
          value={formatDate(evidence.firstDetected)}
        />
        <EvidenceField
          label="Last seen"
          value={formatDate(evidence.lastSeen)}
        />
        <EvidenceField
          label="How it was reached"
          value={
            navigationBreakdown(evidence) ?? describeNavigation(navigationType)
          }
        />
      </div>

      {sampleMatchedUrl && (
        <div className="flex flex-col gap-0.5">
          <span className="text-xs text-muted-foreground">
            Sample matched URL
          </span>
          <span className="break-all font-mono text-xs">
            {sampleMatchedUrl}
          </span>
        </div>
      )}

      <div className="flex items-start gap-2 text-xs text-muted-foreground">
        <Info className="mt-0.5 size-3.5 shrink-0" />
        <span>
          Visits are grouped by how the domain was reached. Real visits exclude
          ads, tracking, and iframe-embedded loads.
        </span>
      </div>
    </div>
  );
}

BrowserToolEvidence.displayName = 'BrowserToolEvidence';
